import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AiQuestionCard from 'app/shared/components/AiQuestionCard';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { UserQuestionStatus } from 'constants/aiChat';

import { dismissUserQuestionAction, respondToUserQuestionAction } from 'store/aiChat/actions';
import { selectAiChatActivePendingUserQuestionByMessageId } from 'store/aiChat/selectors';

// DEV-4392: hosts the ask_user_question card for whichever message currently
// carries the pending question. On the live path that is the streaming
// assistant message (rendered by BotMessage); on reload/reconnect the parked
// AiPendingChanges row is owned by the user message (the only persisted row at
// suspend time — see ChatMessageProcessor), so UserMessage hosts it too.
// Self-contained and keyed by messageId, mirroring ToolConfirmationCard.
const PendingQuestionCard = ({ messageId }) => {
  const dispatch = useDispatch();
  const pendingQuestion = useSelector(state =>
    selectAiChatActivePendingUserQuestionByMessageId(state, messageId),
  );

  const pendingQuestionId = pendingQuestion?.questionId ?? null;
  const isSubmitting =
    pendingQuestion?.status === UserQuestionStatus.SUBMITTING ||
    pendingQuestion?.status === UserQuestionStatus.DISMISSING;

  const onSubmit = useCallback(
    answersJson => {
      if (_.isNil(pendingQuestionId)) {
        return;
      }

      dispatch(
        respondToUserQuestionAction({ messageId, questionId: pendingQuestionId, answersJson }),
      );
    },
    [dispatch, messageId, pendingQuestionId],
  );

  const onDismiss = useCallback(() => {
    if (_.isNil(pendingQuestionId)) {
      return;
    }

    dispatch(dismissUserQuestionAction({ messageId, questionId: pendingQuestionId }));
  }, [dispatch, messageId, pendingQuestionId]);

  if (_.isNil(pendingQuestion)) {
    return null;
  }

  return (
    <AiQuestionCard
      isSubmitting={isSubmitting}
      questions={pendingQuestion.questions}
      onDismiss={onDismiss}
      onSubmit={onSubmit}
    />
  );
};

PendingQuestionCard.propTypes = {
  messageId: PropTypes.string,
};

export default memo(PendingQuestionCard);
